import {
  resolveMemberIdForEligibilityCheck,
  resolvePatientIntakeIdForEligibilityCheck,
  resolvePayorIdForEligibilityCheck,
  saveEligibilityCheckFromResponse,
  stripEligibilityPersistenceMeta,
  type EligibilityCheckPersistContext,
  type EligibilityCheckPersistResult,
} from './intakeEligibilityPersistence'
import { submitPverifyEligibilityCheck } from './pverifyService'
import { submitStediEligibilityCheck } from './stediService'
import type {
  IntakeEligibilityForm,
  PverifyEligibilityBuildOptions,
} from './pverifyEligibilityPayload'

export type EligibilityServiceType = 'stedi' | 'pverify'

export type StediConfig = {
  apiUrl: string
  apiKey: string
}

export type PverifyConfig = {
  apiUrl: string
  clientKey: string
  clientSecret: string
  clientApiId: string
  eligibilityPath: string
}

export type EligibilityServiceConfigs = {
  stedi: StediConfig
  pverify: PverifyConfig
}

/** Defaults are empty; credentials are loaded from Settings (backend DB), not VITE_* env. */
export function buildDefaultServiceConfigs(): EligibilityServiceConfigs {
  return {
    stedi: {
      apiUrl: '',
      apiKey: '',
    },
    pverify: {
      apiUrl: '',
      clientKey: '',
      clientSecret: '',
      clientApiId: '',
      eligibilityPath: '/api/EligibilitySummary',
    },
  }
}

type SubmitEligibilityArgs = {
  service: EligibilityServiceType
  serviceConfigs: EligibilityServiceConfigs
  formData: IntakeEligibilityForm
  options: PverifyEligibilityBuildOptions
  intakeContext: {
    cptHcpcCode: string
    serviceDateFrom: string
    serviceDateTo: string
    patientName: string
    patientDOB?: string
  }
}

export async function submitEligibilityCheck({
  service,
  serviceConfigs,
  formData,
  options,
  intakeContext,
}: SubmitEligibilityArgs): Promise<Record<string, unknown>> {
  if (service === 'pverify') {
    const result = await submitPverifyEligibilityCheck({
      formData,
      options,
      config: serviceConfigs.pverify,
    })
    console.log('[Eligibility Result][pverify]', result)
    return result
  }
  const result = await submitStediEligibilityCheck({
    formData,
    options,
    config: serviceConfigs.stedi,
    intake: intakeContext,
  })
  console.log('[Eligibility Result][stedi]', result)
  return result
}

export type SubmitIntakeEligibilityArgs = SubmitEligibilityArgs & {
  /** When false, skips POST /eligibility-checks. Default true. */
  saveEligibilityCheck?: boolean
  /** Pass when intake is linked to existing patient / payor / intake records. */
  persistContext?: EligibilityCheckPersistContext
}

export type SubmitIntakeEligibilityResult = {
  parsed: Record<string, unknown>
  saved?: EligibilityCheckPersistResult
}

/**
 * Runs Stedi or pVerify eligibility, then saves request/response via POST /eligibility-checks.
 */
export async function submitIntakeEligibilityFlow(
  args: SubmitIntakeEligibilityArgs
): Promise<SubmitIntakeEligibilityResult> {
  const { saveEligibilityCheck = true, persistContext, formData, ...eligibilityArgs } = args
  const rawParsed = await submitEligibilityCheck({
    ...eligibilityArgs,
    formData,
  })
  let saved: EligibilityCheckPersistResult | undefined
  if (saveEligibilityCheck) {
    const memberId =
      persistContext?.patientId?.trim() ||
      resolveMemberIdForEligibilityCheck(formData, rawParsed)
    const payorId =
      persistContext?.payorId?.trim() ||
      resolvePayorIdForEligibilityCheck(rawParsed, formData)
    const patientIntakeId =
      persistContext?.patientIntakeId?.trim() ||
      resolvePatientIntakeIdForEligibilityCheck(rawParsed, formData)
    saved = await saveEligibilityCheckFromResponse(
      rawParsed,
      {
        ...persistContext,
        patientId: memberId,
        payorId,
        patientIntakeId,
      },
      formData
    )
  }
  return {
    parsed: stripEligibilityPersistenceMeta(rawParsed),
    saved,
  }
}
